import dayjs from 'dayjs';
import React, { useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { ReactComponent as GraphHoverIcon } from '../../assets/icons/graph-hover-icon.svg';
import { ReactComponent as GraphActiveIcon } from '../../assets/icons/graph-icon-active.svg';
import { ReactComponent as GraphIcon } from '../../assets/icons/graph-icon.svg';
import { ReactComponent as MoreIcon } from '../../assets/icons/more-icon.svg';
import { ReactComponent as ReportsTableActiveIcon } from '../../assets/icons/textalign-justifycenter-active.svg';
import { ReactComponent as ReportsTableHoverIcon } from '../../assets/icons/textalign-justifycenter-hover-icon.svg';
import { ReactComponent as ReportsTableIcon } from '../../assets/icons/textalign-justifycenter.svg';
import ThemeContext from '../../contexts/theme-context/ThemeContext';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { setNotificationDetails } from '../../redux/slice/NotificationSlice';
import { setPageTitle } from '../../redux/slice/PageTitleSlice';
import { setBookingPointReportUpdate } from '../../redux/slice/ReportBookingPointSlice';
import { setBookingStationReportUpdate } from '../../redux/slice/ReportBookingStationSlice';
import { setChargerPointReportUpdate } from '../../redux/slice/ReportChargerPointSlice';
import { setChargerStationReportUpdate } from '../../redux/slice/ReportChargerStationSlice';
import { setEnergyPointReportUpdate } from '../../redux/slice/ReportEnergyPointSlice';
import { setEnergyStationReportUpdate } from '../../redux/slice/ReportEnergyStationSlice';
import { setMaintenancePointReportUpdate } from '../../redux/slice/ReportMaintenancePointSlice';
import { setMaintenanceStationReportUpdate } from '../../redux/slice/ReportMaintenanceStationSlice';
import { setPerformancePointReportUpdate } from '../../redux/slice/ReportPerformancePointSlice';
import { setPerformanceStationReportUpdate } from '../../redux/slice/ReportPerformanceStationSlice';
import { setRevenuePointReportUpdate } from '../../redux/slice/ReportRevenuePointSlice';
import { setRevenueStationReportUpdate } from '../../redux/slice/ReportRevenueStationSlice';
import {
  setReportsData,
  resetReportUpdateFlag,
  fetchBookMarksList,
  clearDateRange,
} from '../../redux/slice/ReportSlice';
import MyButton from '../../shared-components/button/Button';
import CustomDatePicker from '../../shared-components/custom-date-range/CustomDatePicker';
import CustomPopover from '../../shared-components/custom-pop-over/CustomPopover';
import TimeRangeDropdown from '../../shared-components/custom-time-range-dropdown/TimeRangeDropdown';
import CustomToggleButton from '../../shared-components/custom-toggle-button/CustomToggleButton';
import ModalBox from '../../shared-components/modal-box/ModalBox';
import SelectField from '../../shared-components/select-field/SelectField';
import SuccessNotification from '../../shared-components/success-notification/SuccessNotification';
import { getDateRange } from '../../shared-functions/DataTime';
import reportApi from '../../utils/api/report';

import AlertsReportChart from './components/alertsReport/AlertsReportChart';
import AlertsReportTablePoint from './components/alertsReport/AlertsReportTablePoint';
import AlertsReportTableStation from './components/alertsReport/AlertsReportTableStation';
import BookingReportDurationChart from './components/bookingreport/BookingReportDurationChart';
import BookingReportTablePoint from './components/bookingreport/BookingReportTablePoint';
import BookingReportTableStation from './components/bookingreport/BookingReportTableStation';
import BookingReportTrendChart from './components/bookingreport/BookingReportTrendChart';
import BookmarkOption from './components/BookmarkOption';
import ChargerReportChart from './components/chargerUtilizationReport/ChargerReportChart';
import ChargerReportRevenue from './components/chargerUtilizationReport/ChargerReportRevenueChart';
import ChargerReportTablePoint from './components/chargerUtilizationReport/ChargerReportTablePoint';
import ChargerReportTableStation from './components/chargerUtilizationReport/ChargerReportTableStation';
import EnergyReportDurationChart from './components/energyreport/EnergyReportDurationChart';
import EnergyReportRevenue from './components/energyreport/EnergyReportRevenueChart';
import EnergyReportTablePoint from './components/energyreport/EnergyReportTablePoint';
import EnergyReportTableStation from './components/energyreport/EnergyReportTableStation';
import EnergyReportTrendChart from './components/energyreport/EnergyReportTrendChart';
import MaintenanceReportChart from './components/MaintenanceReport/MaintenanceReportChart';
import MaintenanceReportRevenue from './components/MaintenanceReport/MaintenanceReportRevenueChart';
import MaintenanceReportTablePoint from './components/MaintenanceReport/MaintenanceReportTablePoint';
import MaintenanceReportTableStation from './components/MaintenanceReport/MaintenanceReportTableStation';
import PerformanceReportChart from './components/performanceReport/PerformanceReportChart';
import PerformanceReportTablePoint from './components/performanceReport/PerformanceReportTablePoint';
import PerformanceReportTableStation from './components/performanceReport/PerformanceReportTableStation';
import RevenueReportDurationChart from './components/revenuereport/RevenueReportDurationChart';
import RevenueReportRevenue from './components/revenuereport/RevenueReportRevenueChart';
import RevenueReportTablePoint from './components/revenuereport/RevenueReportTablePoint';
import RevenueReportTableStation from './components/revenuereport/RevenueReportTableStation';
import RevenueReportTrendChart from './components/revenuereport/RevenueReportTrendChart';

interface ReportData {
  reportByType: string;
  startDate: string;
  endDate: string;
  year?: string;
}

interface ModalBoxDetail {
  title: string | null;
  header: boolean;
  content: JSX.Element | null;
  notificationModal: boolean;
  rightCorner: string;
}

/**
 * PowerConsumptionChart component renders a bar chart to display power consumption data.
 * @returns {JSX.Element} - The rendered bar chart component.
 */
function PowerConsumptionChart(): JSX.Element {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('reports');
  const { theme } = useContext(ThemeContext);
  const { page } = useParams<{ page: string }>();
  const [openTab, setOpenTab] = useState(0);
  const [openTabbleorGraph, setOpenTabbleorGraph] = useState(0);
  const [preferenceFilter, setPreferenceFilter] = useState<any>({});
  const [selectPreference, setSelectPreference] = useState<string>('');
  const reportsData = useAppSelector((state) => state.reportData);
  const { filters: reportEnergyStationFilter } = useAppSelector(
    (state) => state.reportEnergyStation,
  );
  // const { filters: reportAlertsStationFilter } = useAppSelector(
  //   (state) => state.reportAlertsStation,
  // );
  const { filters: reportBookingStationFilter } = useAppSelector(
    (state) => state.reportBookingStation,
  );
  const { filters: reportChargerStationFilter } = useAppSelector(
    (state) => state.reportChargerStation,
  );
  const { filters: reportMaintenanceStationFilter } = useAppSelector(
    (state) => state.reportMaintenanceStation,
  );
  const { filters: reportPerformanceStationFilter } = useAppSelector(
    (state) => state.reportPerformanceStation,
  );
  const { filters: reportRevenueStationFilter } = useAppSelector(
    (state) => state.reportRevenueStation,
  );
  const { filters: reportEnergyPointFilter } = useAppSelector((state) => state.reportEnergyPoint);
  // const { filters: reportAlertsPointFilter } = useAppSelector((state) => state.reportAlertsPoint);
  const { filters: reportBookingPointFilter } = useAppSelector((state) => state.reportBookingPoint);
  const { filters: reportChargerPointFilter } = useAppSelector((state) => state.reportChargerPoint);
  const { filters: reportMaintenancePointFilter } = useAppSelector(
    (state) => state.reportMaintenancePoint,
  );
  const { filters: reportPerformancePointFilter } = useAppSelector(
    (state) => state.reportPerformancePoint,
  );
  const { filters: reportRevenuePointFilter } = useAppSelector((state) => state.reportRevenuePoint);
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    header: false,
    content: null,
    notificationModal: false,
    rightCorner: '',
  });

  /**
   * Handle the input change function
   * @param name
   * @param value
   */
  const handleInputChange = (name: string, value: string | number) => {
    dispatch(setReportsData({ name: name as keyof ReportData, value: String(value) }));
    setPreferenceFilter({});
    setSelectPreference('');
  };

  const reportByType = [
    {
      label: t('station'),
      value: 'station',
    },
    {
      label: t('charger'),
      value: 'charger',
    },
  ];

  const PAGE_TO_TAB: Record<
    | 'energyReport'
    | 'revenueReport'
    | 'bookingsReport'
    | 'chargerUtilizationReport'
    | 'performanceReport'
    | 'maintenanceReport',
    number
  > = {
    energyReport: 0,
    revenueReport: 1,
    bookingsReport: 2,
    chargerUtilizationReport: 3,
    performanceReport: 4,
    maintenanceReport: 5,
  };

  /**
   * getIcons
   */
  const getIcons = () => ({
    defaultIcons: [
      theme === 'light' ? <ReportsTableIcon /> : <ReportsTableIcon />,
      theme === 'light' ? <GraphIcon /> : <GraphIcon />,
    ],
    activeIcons: [
      theme === 'light' ? <ReportsTableActiveIcon /> : <ReportsTableActiveIcon />,
      theme === 'light' ? <GraphActiveIcon /> : <GraphActiveIcon />,
    ],
    hoverIcons: [
      theme === 'light' ? <ReportsTableHoverIcon /> : <ReportsTableHoverIcon />,
      theme === 'light' ? <GraphHoverIcon /> : <GraphHoverIcon />,
    ],
  });
  const { defaultIcons, activeIcons, hoverIcons } = getIcons();

  /**
   * Handle the change the page
   * @param index
   */
  const getPageName = (index: number) => {
    setOpenTab(index);
    let pageName: string = '';
    let filters: any[] = [];
    switch (index) {
      case 0:
        pageName = 'energyReport';
        filters =
          reportsData.reportByType === 'station'
            ? reportEnergyStationFilter
                .filter((filter) => filter.value.length > 0)
                .map((filter) => ({
                  label: filter.name,
                  value: filter.value,
                  field: filter.key,
                }))
            : reportEnergyPointFilter
                .filter((filter) => filter.value.length > 0)
                .map((filter) => ({
                  label: filter.name,
                  value: filter.value,
                  field: filter.key,
                }));
        break;
      case 1:
        pageName = 'revenueReport';
        filters =
          reportsData.reportByType === 'station'
            ? reportRevenueStationFilter
                .filter((filter) => filter.value.length > 0)
                .map((filter) => ({
                  label: filter.name,
                  value: filter.value,
                  field: filter.key,
                }))
            : reportRevenuePointFilter
                .filter((filter) => filter.value.length > 0)
                .map((filter) => ({
                  label: filter.name,
                  value: filter.value,
                  field: filter.key,
                }));
        break;
      case 2:
        pageName = 'bookingsReport';
        filters =
          reportsData.reportByType === 'station'
            ? reportBookingStationFilter
                .filter((filter) => filter.value.length > 0)
                .map((filter) => ({
                  label: filter.name,
                  value: filter.value,
                  field: filter.key,
                }))
            : reportBookingPointFilter
                .filter((filter) => filter.value.length > 0)
                .map((filter) => ({
                  label: filter.name,
                  value: filter.value,
                  field: filter.key,
                }));
        break;
      case 3:
        pageName = 'chargerUtilizationReport';
        filters =
          reportsData.reportByType === 'station'
            ? reportChargerStationFilter
                .filter((filter) => filter.value.length > 0)
                .map((filter) => ({
                  label: filter.name,
                  value: filter.value,
                  field: filter.key,
                }))
            : reportChargerPointFilter
                .filter((filter) => filter.value.length > 0)
                .map((filter) => ({
                  label: filter.name,
                  value: filter.value,
                  field: filter.key,
                }));
        break;
      case 4:
        pageName = 'performanceReport';
        filters =
          reportsData.reportByType === 'station'
            ? reportPerformanceStationFilter
                .filter((filter) => filter.value.length > 0)
                .map((filter) => ({
                  label: filter.name,
                  value: filter.value,
                  field: filter.key,
                }))
            : reportPerformancePointFilter
                .filter((filter) => filter.value.length > 0)
                .map((filter) => ({
                  label: filter.name,
                  value: filter.value,
                  field: filter.key,
                }));
        break;
      case 5:
        pageName = 'maintenanceReport';
        filters =
          reportsData.reportByType === 'station'
            ? reportMaintenanceStationFilter
                .filter((filter) => filter.value.length > 0)
                .map((filter) => ({
                  label: filter.name,
                  value: filter.value,
                  field: filter.key,
                }))
            : reportMaintenancePointFilter
                .filter((filter) => filter.value.length > 0)
                .map((filter) => ({
                  label: filter.name,
                  value: filter.value,
                  field: filter.key,
                }));
        break;
      // case 6:
      //   pageName = 'alertsReport';
      //   filters =
      //     reportsData.reportByType === 'station'
      //       ? reportAlertsStationFilter
      //           .filter((filter) => filter.value.length > 0)
      //           .map((filter) => ({
      //             label: filter.name,
      //             value: filter.value,
      //             field: filter.key,
      //           }))
      //       : reportAlertsPointFilter
      //           .filter((filter) => filter.value.length > 0)
      //           .map((filter) => ({
      //             label: filter.name,
      //             value: filter.value,
      //             field: filter.key,
      //           }));
      //   break;

      default:
        break;
    }
    return { pageName, filters };
  };

  /**
   * Get the all book marks
   */
  const getAllBookMarks = (name: string) => {
    dispatch(fetchBookMarksList({ entity: name, accountCustomId: reportsData?.accountId }));
  };

  /**
   * Add Bookmark function
   */
  const addBookmark = (bookMarkName: string | number) => {
    const pageName = getPageName(openTab);
    const request = {
      name: bookMarkName,
      entity: pageName.pageName || '',
      filters: pageName.filters || [],
    };
    if (pageName.filters.length > 0) {
      reportApi
        .addBookMark(request)
        .then(() => {
          getAllBookMarks(pageName.pageName);
          setModalBoxDetail({
            title: '',
            header: false,
            rightCorner: '',
            content: (
              <SuccessNotification
                title="Bookmark Added successfully..."
                description=""
                notificationCloseTimer={3000}
                onNotificationClose={() => {
                  setModalBoxStatus('close');
                }}
              />
            ),
            notificationModal: true,
          });
        })
        .catch((error) => {
          dispatch(
            setNotificationDetails({
              type: 'error',
              notificationDetails: {
                statusCode: error?.response?.data?.code || 200,
                message: error?.response?.data?.message || 'Something went wrong!',
              },
            }),
          );
        });
    } else {
      dispatch(
        setNotificationDetails({
          type: 'error',
          notificationDetails: {
            statusCode: 200,
            message: 'Something went wrong!',
          },
        }),
      );
    }
  };

  /**
   * Close the modal box
   */
  const close = () => {
    setModalBoxStatus('close');
  };

  /**
   * Handle the openAddFacilityModal function
   */
  const openAddVlbcModal = () => {
    setModalBoxDetail({
      title: t('saveAsBookmark'),
      content: <BookmarkOption addBookmark={addBookmark} close={close} />,
      notificationModal: false,
      header: true,
      rightCorner: '',
    });
    setModalBoxStatus('open');
  };
  /**
   * Handling select field changes bookmark
   * @param name
   * @param value
   */
  const handleSelectChange = (name: string, value: string) => {
    const pageName = getPageName(openTab);
    const bookmarkFilter = reportsData.allBookmarks.find(
      (bookmark) => bookmark._id === value,
    )?.filters;
    setSelectPreference(value);
    setPreferenceFilter({
      pageName: pageName.pageName,
      filter: bookmarkFilter,
    });
  };
  /**
   * Handle the change the page and graph
   */
  const changePageHandleandGraph = (index: number) => {
    setOpenTabbleorGraph(index);
  };

  /**
   * Handle the change the page
   * @param index
   */
  const changePageHandle = (index: number) => {
    setOpenTab(index);
    let pageName = '';
    switch (index) {
      case 0:
        pageName = 'energyReport';
        break;
      case 1:
        pageName = 'revenueReport';
        break;
      case 2:
        pageName = 'bookingsReport';
        break;
      case 3:
        pageName = 'chargerUtilizationReport';
        break;
      case 4:
        pageName = 'performanceReport';
        break;
      case 5:
        pageName = 'maintenanceReport';
        break;
      // case 6:
      //   pageName = 'alertsReport';
      //   break;

      default:
        break;
    }
    getAllBookMarks(pageName);
    window.history.pushState({}, '', `${pageName}`);
  };

  /**
   *  // Create the leftTopContent JSX
   */
  const renderLeftTopContent = () => (
    <div className="left-top-content">
      <SelectField
        inputLabel={t('reportBy')}
        name="reportByType"
        placeholder={t('selectReportBy')}
        selectedValue={reportsData.reportByType}
        options={reportByType}
        onChangeValue={handleInputChange}
      />
      <CustomDatePicker
        inputLabel={t('startDate')}
        value={
          dayjs(reportsData.reportFilter.startDate, 'YYYY-MM-DD').isValid()
            ? reportsData.reportFilter.startDate
            : ''
        }
        name="startDate"
        onChangeValue={handleInputChange}
        placeholder={t('DD/MM/YYYY')}
      />
      <CustomDatePicker
        placeholder={t('DD/MM/YYYY')}
        inputLabel={t('endDate')}
        value={
          dayjs(reportsData.reportFilter.endDate, 'YYYY-MM-DD').isValid()
            ? reportsData.reportFilter.endDate
            : ''
        }
        name="endDate"
        onChangeValue={handleInputChange}
      />

      <div className="custom-popover-wrapper">
        <CustomPopover
          clickableElement={
            <div className="custom-popover-button">
              <MyButton buttonType="none" label="" onClickFunc={() => {}} svgIcon={<MoreIcon />} />
            </div>
          }
          popOverContent={
            <TimeRangeDropdown
              selected={reportsData.reportFilter.year || ''}
              onSelect={(value: string) => {
                const { startDate, endDate } = getDateRange(value.toLowerCase());

                // Safely format the values
                if (startDate.isValid() && endDate.isValid()) {
                  dispatch(
                    setReportsData({ name: 'startDate', value: startDate.format('YYYY-MM-DD') }),
                  );
                  dispatch(
                    setReportsData({ name: 'endDate', value: endDate.format('YYYY-MM-DD') }),
                  );
                  dispatch(setReportsData({ name: 'year', value }));
                } else {
                  console.error('Invalid startDate or endDate:', startDate, endDate);
                }
              }}
            />
          }
        />
      </div>
    </div>
  );

  useEffect(() => {
    if (reportsData.isReportUpdated) {
      dispatch(setEnergyPointReportUpdate(true));
      dispatch(setEnergyStationReportUpdate(true));
      dispatch(setRevenuePointReportUpdate(true));
      dispatch(setRevenueStationReportUpdate(true));
      dispatch(setBookingStationReportUpdate(true));
      dispatch(setBookingPointReportUpdate(true));
      dispatch(setChargerStationReportUpdate(true));
      dispatch(setChargerPointReportUpdate(true));
      dispatch(setPerformanceStationReportUpdate(true));
      dispatch(setPerformancePointReportUpdate(true));
      dispatch(setMaintenanceStationReportUpdate(true));
      dispatch(setMaintenancePointReportUpdate(true));
      dispatch(resetReportUpdateFlag());
    }
  }, [reportsData.isReportUpdated]);

  useEffect(() => {
    switch (page) {
      case 'energyReport':
        changePageHandle(0);
        break;
      case 'revenueReport':
        changePageHandle(1);
        break;
      case 'bookingsReport':
        changePageHandle(2);
        break;
      case 'chargerUtilizationReport':
        changePageHandle(3);
        break;
      case 'performanceReport':
        changePageHandle(4);
        break;
      case 'maintenanceReport':
        changePageHandle(5);
        break;
      // case 'alertsReport':
      //   changePageHandle(5);
      //   break;

      default:
        break;
    }
  }, []);

  useEffect(() => {
    dispatch(setPageTitle(t(`${page}`)));
    setOpenTab(PAGE_TO_TAB[page as keyof typeof PAGE_TO_TAB] ?? 0);
  }, [page, t]);

  /**
   * Clears the clearDateRange , setting it to null.
   */
  const clearDateRanges = () => {
    dispatch(clearDateRange());
    setSelectPreference('');
  };

  return (
    <div className="report-chart-wrap">
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        modalBoxAnimation="modalAnimateToLeft"
        notification={modalBoxDetail.notificationModal}
        header={modalBoxDetail.header}
        rightCorner={modalBoxDetail.rightCorner}
        version="v2"
      />
      <div className="report-chart-header">
        <div className="page-title-wrap">
          <div className="tab-graph-container">
            <CustomToggleButton
              labels={['', '']}
              onToggle={changePageHandleandGraph}
              defaultActiveIndex={openTabbleorGraph}
              icons={defaultIcons}
              activeIcons={activeIcons}
              hoverIcons={hoverIcons}
            />
          </div>
          <div className="page-title-right-wrap">
            <div className="page-title-rights">
              <div className="top-right-content">
                <SelectField
                  name="selectPreference"
                  options={reportsData.allBookmarks.map((values: any) => ({
                    label: values.name,
                    value: values._id,
                  }))}
                  selectedValue={selectPreference}
                  onChangeValue={handleSelectChange}
                  placeholder={t('selectPreference')}
                />
              </div>
            </div>
            <div className="page-title-right">
              <MyButton
                label={t('savePreference')}
                buttonType="submit"
                onClickFunc={openAddVlbcModal}
              />
            </div>
          </div>
        </div>
        {/* <PageTitle
          title={t('')}
          buttonLabel={t('savePreference')}
          buttonType="submit"
          onClickButton={openAddVlbcModal}
          topRightContent={
            <div className="top-right-content">
              <SelectField
                name="selectPreference"
                options={reportsData.allBookmarks.map((values: any) => ({
                  label: values.name,
                  value: values._id,
                }))}
                selectedValue={selectPreference}
                onChangeValue={handleSelectChange}
                placeholder={t('selectPreference')}
              />
            </div>
          }
        /> */}
      </div>
      <div className="report-tabs-container">
        {/* <div className="tab-table-container">
          <CustomToggleButton
            labels={[
              t('energyReport'),
              t('revenueReport'),
              t('bookingsReport'),
              t('chargerUtilizationReport'),
              t('performanceReport'),
              t('maintenanceReport'),
              // 'Alerts Report',
            ]}
            onToggle={changePageHandle}
            defaultActiveIndex={openTab}
            version="v2"
          />
        </div> */}
      </div>

      <div className="table-container">
        {openTab === 0 &&
          (openTabbleorGraph === 0 ? (
            <>
              {reportsData.reportByType === 'station' && (
                <EnergyReportTableStation
                  leftTopContent={renderLeftTopContent()}
                  preferenceFilter={preferenceFilter}
                  clearDateRange={clearDateRanges}
                />
              )}
              {reportsData.reportByType === 'charger' && (
                <EnergyReportTablePoint
                  leftTopContent={renderLeftTopContent()}
                  preferenceFilter={preferenceFilter}
                  clearDateRange={clearDateRanges}
                />
              )}
              <div className="report-chart">
                {reportsData.activePinName &&
                  reportsData.activePinName.includes('powerConsumedByRevenueTyre') && (
                    <EnergyReportRevenue />
                  )}
                {reportsData.activePinName &&
                  reportsData.activePinName.includes('powerConsumedByGuestVsSchedule') && (
                    <EnergyReportDurationChart />
                  )}
                {reportsData.activePinName &&
                  reportsData.activePinName.includes('powerConsumed') && <EnergyReportTrendChart />}
              </div>
            </>
          ) : (
            <div className="report-chart">
              <EnergyReportRevenue />
              <EnergyReportDurationChart />
              <EnergyReportTrendChart />
            </div>
          ))}
        {openTab === 1 &&
          (openTabbleorGraph === 0 ? (
            <>
              {reportsData.reportByType === 'station' && (
                <RevenueReportTableStation
                  leftTopContent={renderLeftTopContent()}
                  preferenceFilter={preferenceFilter}
                  clearDateRange={clearDateRanges}
                />
              )}
              {reportsData.reportByType === 'charger' && (
                <RevenueReportTablePoint
                  leftTopContent={renderLeftTopContent()}
                  preferenceFilter={preferenceFilter}
                  clearDateRange={clearDateRanges}
                />
              )}
              <div className="report-chart">
                {reportsData.activePinName &&
                  reportsData.activePinName.includes('totalRevenue') && <RevenueReportTrendChart />}
                {reportsData.activePinName &&
                  reportsData.activePinName.includes('totalRevenueBySchedule') && (
                    <RevenueReportDurationChart />
                  )}
                {reportsData.activePinName &&
                  reportsData.activePinName.includes('totalRevenueByRevenueTyre') && (
                    <RevenueReportRevenue />
                  )}
              </div>
            </>
          ) : (
            <div className="report-chart">
              <RevenueReportTrendChart />
              <RevenueReportDurationChart />
              <RevenueReportRevenue />
            </div>
          ))}
        {openTab === 2 &&
          (openTabbleorGraph === 0 ? (
            <>
              {reportsData.reportByType === 'station' && (
                <BookingReportTableStation
                  leftTopContent={renderLeftTopContent()}
                  preferenceFilter={preferenceFilter}
                  clearDateRange={clearDateRanges}
                />
              )}
              {reportsData.reportByType === 'charger' && (
                <BookingReportTablePoint
                  leftTopContent={renderLeftTopContent()}
                  preferenceFilter={preferenceFilter}
                  clearDateRange={clearDateRanges}
                />
              )}
              <div className="report-chart">
                {reportsData.activePinName &&
                  reportsData.activePinName.includes('scheduleBooking') && (
                    <BookingReportTrendChart />
                  )}
                {reportsData.activePinName &&
                  reportsData.activePinName.includes('scheduleBookingCompletedVsCanceled') && (
                    <BookingReportDurationChart />
                  )}
              </div>
            </>
          ) : (
            <div className="report-chart">
              <BookingReportTrendChart />
              <BookingReportDurationChart />
            </div>
          ))}
        {openTab === 3 &&
          (openTabbleorGraph === 0 ? (
            <>
              {reportsData.reportByType === 'station' && (
                <ChargerReportTableStation
                  leftTopContent={renderLeftTopContent()}
                  preferenceFilter={preferenceFilter}
                  clearDateRange={clearDateRanges}
                />
              )}
              {reportsData.reportByType === 'charger' && (
                <ChargerReportTablePoint
                  leftTopContent={renderLeftTopContent()}
                  preferenceFilter={preferenceFilter}
                  clearDateRange={clearDateRanges}
                />
              )}
              <div className="report-chart">
                {reportsData.activePinName &&
                  reportsData.activePinName.includes('uptimeVsDowntime') && <ChargerReportChart />}
                {reportsData.activePinName &&
                  reportsData.activePinName.includes('uptimeVsInuse') && <ChargerReportRevenue />}
              </div>
            </>
          ) : (
            <div className="report-chart">
              <ChargerReportChart />
              <ChargerReportRevenue />
            </div>
          ))}
        {openTab === 4 &&
          (openTabbleorGraph === 0 ? (
            <>
              {reportsData.reportByType === 'station' && (
                <PerformanceReportTableStation
                  leftTopContent={renderLeftTopContent()}
                  preferenceFilter={preferenceFilter}
                  clearDateRange={clearDateRanges}
                />
              )}
              {reportsData.reportByType === 'charger' && (
                <PerformanceReportTablePoint
                  leftTopContent={renderLeftTopContent()}
                  preferenceFilter={preferenceFilter}
                  clearDateRange={clearDateRanges}
                />
              )}
              <div className="report-chart">
                {reportsData.activePinName && reportsData.activePinName.includes('performance') && (
                  <PerformanceReportChart />
                )}
              </div>
            </>
          ) : (
            <div className="report-chart">
              <PerformanceReportChart />
            </div>
          ))}
        {openTab === 5 &&
          (openTabbleorGraph === 0 ? (
            <>
              {reportsData.reportByType === 'station' && (
                <MaintenanceReportTableStation
                  leftTopContent={renderLeftTopContent()}
                  preferenceFilter={preferenceFilter}
                  clearDateRange={clearDateRanges}
                />
              )}
              {reportsData.reportByType === 'charger' && (
                <MaintenanceReportTablePoint
                  leftTopContent={renderLeftTopContent()}
                  preferenceFilter={preferenceFilter}
                  clearDateRange={clearDateRanges}
                />
              )}
              <div className="report-chart">
                {reportsData.activePinName &&
                  reportsData.activePinName.includes('maintenanceScheduleStatus') && (
                    <MaintenanceReportRevenue />
                  )}
                {reportsData.activePinName &&
                  reportsData.activePinName.includes('maintenanceScheduleTask') && (
                    <MaintenanceReportChart />
                  )}
              </div>
            </>
          ) : (
            <div className="report-chart">
              <MaintenanceReportRevenue />
              <MaintenanceReportChart />
            </div>
          ))}
        {openTab === 6 &&
          (openTabbleorGraph === 0 ? (
            <>
              {reportsData.reportByType === 'station' && (
                <AlertsReportTableStation
                  leftTopContent={renderLeftTopContent()}
                  preferenceFilter={preferenceFilter}
                  clearDateRange={clearDateRanges}
                />
              )}
              {reportsData.reportByType === 'charger' && (
                <AlertsReportTablePoint
                  leftTopContent={renderLeftTopContent()}
                  preferenceFilter={preferenceFilter}
                  clearDateRange={clearDateRanges}
                />
              )}
              <div className="report-chart">
                {reportsData.activePinName &&
                  reportsData.activePinName.includes('scheduleBooking') && <AlertsReportChart />}
              </div>
            </>
          ) : (
            <div className="report-chart">
              <AlertsReportChart />
            </div>
          ))}
      </div>
    </div>
  );
}

export default PowerConsumptionChart;
